<template>
  <div class="table-container">
    <table class="table">
      <thead>
      <tr>
        <th class="w-150"></th>
        <th class="text-center">
          Application #{{ application.id }}<br>
          <span v-html="application.status"></span><br>
          <small>Before / after</small>
        </th>
        <th v-for="duplicate in duplicates" class="text-center">
          Application #{{ duplicate.id }}<br>
          <span v-html="duplicate.status"></span>
        </th>
      </tr>
      </thead>

      <tbody>
      <tr v-for="(field, fieldKey) in fields">

        <!-- Original Application text fields (with photo) and difference -->

        <td class="font-bold text-right left-header">{{ field['name'] }}</td>

        <application-field :application="application"
                           :field-key="fieldKey"
                           :field="field"
                           :override-fields="data.overrideFields"
                           :selected-fields="data.selectedFields"
                           :duplicates="duplicates"
                           :duplicate-ids="duplicateIds"
                           :phone-mask="phoneMask"
                           :errors="data.errors"/>

        <!-- Columns with Duplicates text fields (with photo) -->

        <td v-for="duplicate in duplicates">

          <duplicate-field :application="application"
                            :field-key="fieldKey"
                            :field="field"
                            :override-fields="data.overrideFields"
                            :selected-fields="data.selectedFields"
                            :duplicate="duplicate"
                            :duplicate-ids="duplicateIds"/>
        </td>
      </tr>

      <tr v-for="(table, tableKey) in tables">

        <!-- Original Application tables and difference (field experience, desk experience, certifications, licenses) -->

        <td class="font-bold text-right left-header">{{ table['name'] }}</td>

        <application-table :table="table"
                            :tableKey="tableKey"
                            :application-id="application.id"
                            :collected-table-data="collectedTableData(tableKey)"
                            :collected-original-tables="collectedOriginalTables[tableKey]"
                            :original-table-in-selected="originalTableInSelected(tableKey)"
                            @selected-table-change="changeSelectedTables"
                            @selected-table-item-change="changeSelectedTableItems"/>

        <!-- Columns with Duplicates tables (field experience, desk experience, certifications, licenses) -->

        <td v-for="(tableData, id) in tablesData">
          <duplicate-table :table="table"
                            :table-key="tableKey"
                            :table-id="parseInt(id)"
                            :table-data="tableData"
                            :table-is-resolved="tableIsResolved(id, tableKey)"
                            @selected-table-change="changeSelectedTables"/>
        </td>
      </tr>

      <tr>
        <!-- Original Application attachments and difference -->

        <td class="font-bold text-right left-header">Attachments</td>

        <application-attachments :application-attachments="applicationAttachments"
                                 :application-id="parseInt(application.id)"
                                 :collected-attachments="collectedAttachments"
                                 :collected-original-attachments="collectedOriginalAttachments"
                                 :original-attachments-in-selected="originalAttachmentsInSelected"
                                 :initial-selected-attachment-items="data.selectedAttachmentItems"
                                 @selected-attachments-change="changeSelectedAttachments"
                                 @selected-attachments-item-change="changeSelectedAttachmentItems"/>

        <!-- Columns with Duplicates attachments -->

        <td v-for="(attachmentsObject, id) in duplicatesAttachments">
          <duplicate-attachments :attachments-object="attachmentsObject"
                                 :id="parseInt(id)"
                                 :application-attachments="applicationAttachments"
                                 @selected-attachments-change="changeSelectedAttachments"/>
        </td>
      </tr>
      </tbody>
    </table>

    <hr>

    <div class="row">
      <div class="col-md-12">
        <div class="m-r-sm pull-right">

          <simple-confirmation-modal
            modal-id="cancel-confirmation-modal"
            title="Cancel Merging Application"
            message="Are you sure you want to stop merging?"
            @confirm="onConfirmCancelMerge">

            <button type="button" class="btn btn-default mr-1" v-modal="'cancel-confirmation-modal'">
              <i class="fa fa-fw fa-ban"></i>Cancel
            </button>
          </simple-confirmation-modal>

          <application-merge-preview-modal
            modal-id="application-merge-modal"
            :application="mergedApplication"
            :attachments="selectedCollectedAttachments"
            :overrideFields="data.overrideFields"
            :selectedFields="data.selectedFields"
            :selectedTables="data.selectedTables"
            :selectedAttachments="data.selectedAttachments"
            :action="actionUrl"
            :tables="tables"
            :fields="fields"
            :duplicate-ids="duplicateIds"
            @success="onSubmitSuccess"
            @error="onSubmitError">

            <button type="button" class="btn btn-primary" v-modal="'application-merge-modal'">
              <i class="fa fa-fw fa-save"></i>Preview
            </button>
          </application-merge-preview-modal>
        </div>
      </div>
    </div>

    <slot name="hidden"/>
  </div>

</template>

<script>

import ApplicationField from "./application_merge/ApplicationField";
import DuplicateField from "./application_merge/DuplicateField";
import ApplicationTable from "./application_merge/ApplicationTable";
import DuplicateTable from "./application_merge/DuplicateTable";
import ApplicationAttachments from "./application_merge/ApplicationAttachments";
import DuplicateAttachments from "./application_merge/DuplicateAttachments";
import ApplicationsMerge from "../mixins/ApplicationsMerge";

/**
 *
 * Component used to merge the applications.
 *
 * Input properties:
 *
 * application - original application object.
 * applicationAttachments - original application attachments sorted by the type.
 * duplicates - list of duplicates.
 * duplicatesAttachments - list of duplicate attachments.
 * fields - description on application model fields.
 * tables - description on application model tables (field experience, desk experience, licenses, certifications).
 * tablesData - description on duplicates model tables (field experience, desk experience, licenses, certifications).
 * actionUrl - action to submit merging result.
 * cancelUrl - action to cancel merging.
 * redirectUrl - action after success merging.
 *
 *
 * Usage examples:
 *
 * <applications-merge :application="{{ json_encode($applicationData) }}"
                       :application-attachments="{{ json_encode($applicationAttachments) }}"
                       :duplicates="{{ json_encode($duplicates) }}"
                       :duplicates-attachments="{{ json_encode($duplicatesAttachments) }}"
                       :fields="{{ json_encode($fields) }}"
                       :tables="{{ json_encode($tables) }}"
                       :tables-data="{{ json_encode($tablesData) }}"
                       action-url="{{ route('adjusters.applications.merge', ['application' => $applicationId]) }}"
                       cancel-url="{{ route('adjusters.applications.show', ['application' => $applicationId]) }}"
                       redirect-url="{{ route('adjusters.applications.show', ['application' => $applicationId]) }}">
 * </applications-merge>
 */

export default {
  mixins: [ApplicationsMerge],

  beforeMount() {

    // Initialize selectedFields object to store user selections.
    let selectedFieldsObject = {};
    Object.keys(this.fields).forEach((fieldKey) => {
      this.data.fieldKeys.push(fieldKey);
      if (!this.data.selectedFields[fieldKey]) {
        selectedFieldsObject[fieldKey] = [];
      }
    });
    this.data.selectedFields = selectedFieldsObject;

    // Initialize overrideFields object to store overridden values by user.
    let overrideFieldsObject = {};
    Object.keys(this.fields).forEach((fieldKey) => {
      if (!this.data.overrideFields[fieldKey]) {
        overrideFieldsObject[fieldKey] = this.application[fieldKey];
      }
    });
    this.data.overrideFields = overrideFieldsObject;

    // Initialize selectedTables object to store user selections for field experience, desk experience, certifications and licenses.
    let selectedTablesObject = {};
    Object.keys(this.tables).forEach((tableKey) => {
      if (!this.data.selectedTables[tableKey]) {
        selectedTablesObject[tableKey] = [this.application.id];
      }
    });
    this.data.selectedTables = selectedTablesObject;

    // Initialize selectedAttachments object to store user selections for attachments.
    let selectedAttachmentsObject = [];

    if (!this.data.selectedAttachments.length) {
      selectedAttachmentsObject = [this.application.id];
    }
    this.data.selectedAttachments = selectedAttachmentsObject;

    this.data.errors = {};
  },

  props: {
    application: {
      type: Object,
      required: true
    },
    applicationAttachments: {
      type: Object,
      required: false,
      default: () => {
        return {};
      },
    },
    duplicates: {
      type: Object,
      required: true
    },
    duplicatesAttachments: {
      type: Object,
      required: false,
      default: () => {
        return {};
      },
    },
    fields: {
      type: Object,
      required: true,
    },
    tables: {
      type: Object,
      required: true,
    },
    tablesData: {
      type: Object,
      required: true,
    },
    actionUrl: {
      type: String,
      required: true,
    },
    cancelUrl: {
      type: String,
      required: true,
    },
    redirectUrl: {
      type: String,
      required: true,
    },
    phoneMask: {
      type: Object,
      default: null
    },
  },

  components: {
    ApplicationField,
    DuplicateField,
    ApplicationTable,
    DuplicateTable,
    ApplicationAttachments,
    DuplicateAttachments,
  },

  data() {
    return {
      data: {
        overrideFields: {},
        selectedFields: {},
        selectedTables: {},
        selectedTableItems: {
          licenses: {},
          certifications: {},
          field_experience: {},
          desk_experience: {},
        },
        selectedAttachments: [],
        selectedAttachmentItems: {},
        fieldKeys: [],
        errors: {}
      },
    };
  },

  watch: {
    'data.selectedTables': {
      handler() {
        Object.keys(this.tables).forEach(tableKey => {
          let applicationTables = this.application[tableKey];
          let old = this.data.selectedTableItems[tableKey];

          this.data.selectedTableItems[tableKey] = {};

          this.data.selectedTables[tableKey].forEach((id) => {
            if (id === this.application.id)
              applicationTables.forEach(row => {
                this.data.selectedTableItems[tableKey][row.id] = old[row.id] ?? true;
              });
            else
              this.tablesData[id][tableKey].forEach(row => {
                this.data.selectedTableItems[tableKey][row.id] = old[row.id] ?? true;
              });
          });
        });
      },
      deep: true
    },

    'data.selectedAttachments': {
      handler() {
        let old = this.data.selectedAttachmentItems;

        let appendSelectedAttachmentItems = (fileType, data) => {
          if (fileType !== undefined)
            data.forEach((item) => {
              this.data.selectedAttachmentItems[item.id] = old[item.id] ?? true;
            })
        };

        this.data.selectedAttachmentItems = {};

        this.data.selectedAttachments.forEach((id) => {
          if (id === this.application.id)
            Object.keys(this.applicationAttachments).forEach(fileType => {
              appendSelectedAttachmentItems(fileType, this.applicationAttachments[fileType]);
            });
          else
            Object.keys(this.duplicatesAttachments[id]).forEach(fileType => {
              appendSelectedAttachmentItems(fileType, this.duplicatesAttachments[id][fileType]);
            });
        });
      },
      deep: true
    },
  },

  computed: {

    mergedApplication() {
      let merged = _.cloneDeep(this.application);

      this.data.fieldKeys.forEach((fieldKey) => {
        merged[fieldKey] = this.fieldValue(fieldKey, this.data.overrideFields, this.data.selectedFields);
      });

      Object.keys(this.tables).forEach(tableKey => {
        merged[tableKey] = this.collectedTableData(tableKey).filter(item => this.data.selectedTableItems[tableKey][item.id]);
      });

      return merged;
    },

    collectedOriginalTables() {
      return {
        field_experience: this.collectedFieldExperience(),
        desk_experience: this.collectedDeskExperience(),
        certifications: this.collectedCertifications(),
        licenses: this.collectedLicenses(),
      };
    },

    // Collect attachments data based on original rows and duplicate selections.
    collectedAttachments() {
      let attachments = {};

      if (this.data.selectedAttachments.filter(item => item === this.application.id).length)

        // Add original attachments.
        Object.keys(this.applicationAttachments).forEach((fileType) => {
          this.appendAttachments(attachments, fileType, this.applicationAttachments[fileType]);
        });

        // Add attachments from selected duplicates.
        this.data.selectedAttachments.forEach((id) => {
          if (id !== this.application.id)
            Object.keys(this.duplicatesAttachments[id]).forEach((fileType) => {
              this.appendAttachments(attachments, fileType, this.duplicatesAttachments[id][fileType]);
            });
        });

      return attachments;
    },

    // Collect attachment items based on original rows and duplicate selections.
    selectedCollectedAttachments() {

      let selectedAttachments = {};

      Object.keys(this.collectedAttachments).forEach(fileType => {
        let typeAttachments = this.collectedAttachments[fileType].filter(item => this.data.selectedAttachmentItems[item.id]);

        if (typeAttachments.length)
          selectedAttachments[fileType] = typeAttachments;
      });

      return selectedAttachments;
    },

    collectedOriginalAttachments() {
      let attachments = {};

      Object.keys(this.applicationAttachments).forEach((fileType) => {
        this.appendAttachments(attachments, fileType, this.applicationAttachments[fileType]);
      });

      return attachments;
    },

    originalAttachmentsInSelected() {
      return Boolean(this.data.selectedAttachments.filter(item => item === this.application.id).length);
    },

    // Collect duplicate ids.
    duplicateIds() {

      let ids = [];

      Object.keys(this.duplicates).forEach(function (duplicateId) {
        ids.push(parseInt(duplicateId));
      });

      return ids;
    }
  },

  methods: {

    changeSelectedTables(tableKey, id, value) {
      let selectedTables = this.data.selectedTables[tableKey];

      if (value && !selectedTables.includes(id))
        selectedTables.push(id);
      else if (!value && selectedTables.includes(id))
        selectedTables = selectedTables.filter(tableId => tableId !== id);

      this.data.selectedTables[tableKey] = selectedTables;
    },

    changeSelectedTableItems(tableKey, id, value) {
      this.data.selectedTableItems[tableKey][id] = value;
    },

    changeSelectedAttachments(id, value) {
      let selectedAttachments = this.data.selectedAttachments;

      if (value && !selectedAttachments.includes(id))
        selectedAttachments.push(id);
      else if (!value && selectedAttachments.includes(id))
        selectedAttachments = selectedAttachments.filter(attachmentsId => attachmentsId !== id);

      this.data.selectedAttachments = selectedAttachments;
    },

    changeSelectedAttachmentItems(id, value) {
      this.data.selectedAttachmentItems[id] = value;
    },

    appendAttachments(attachments, fileType, data, added) {
      if (attachments[fileType] === undefined) {
        attachments[fileType] = [];
      }

      data.forEach((item) => {
        if (!added)
          attachments[fileType].push(item);
        else
          attachments[fileType].push(Object.assign({'_added': true}, item));
      });
    },

    // Collect specified table's data based on original rows and duplicate selections.
    collectedTableData(tableKey) {

      let applicationTableData = [];

      if (this.data.selectedTables[tableKey].filter(item => item === this.application.id).length) {
        if (tableKey === 'field_experience')
          applicationTableData = this.collectedFieldExperience();
        else if (tableKey === 'desk_experience')
          applicationTableData = this.collectedDeskExperience();
        else if (tableKey === 'certifications')
          applicationTableData = this.collectedCertifications();
        else if (tableKey === 'licenses')
          applicationTableData = this.collectedLicenses();
      }

      // // Add row if it is unique.
      // let pushTableRow = function (newRow) {
      //
      //   let isSame = false;
      //   let tableKeys = Object.keys(newRow);
      //
      //   applicationTableData.some(function (presentRow) {
      //     isSame = true;
      //     tableKeys.some(function (_key) {
      //       if (!_key.startsWith('_') && newRow[_key] !== presentRow[_key]) {
      //         isSame = false;
      //         return true;
      //       }
      //     });
      //     // stop iterating.
      //     return isSame;
      //   });
      //
      //   if (!isSame) {
      //     applicationTableData.push(newRow);
      //   }
      // }

      this.data.selectedTables[tableKey].forEach((id) => {
        if (id !== this.application.id)
          this.tablesData[id][tableKey].forEach((row, i) => {
            // pushTableRow(Object.assign({'_added': true}, row));
            applicationTableData.push(row);
          });
      });

      return applicationTableData;
    },

    // Collect field experience table data.
    collectedFieldExperience() {

      let data = [];

      this.application.field_experience.forEach((row) => {
        data.push({id: row.id, name: row.experience.name, years: row.years});
      });

      return data;
    },

    // Collect desk experience table data.
    collectedDeskExperience() {

      let data = [];

      this.application.desk_experience.forEach((row) => {
        data.push({id: row.id, name: row.experience.name, years: row.years});
      });

      return data;
    },

    // Collect certifications table data.
    collectedCertifications() {

      let data = [];

      this.application.certifications.forEach((row) => {
        data.push({id: row.id, name: row.certification.name});
      });

      return data;
    },

    // Collect licenses table data.
    collectedLicenses() {

      let data = [];

      this.application.licenses.forEach((row, i) => {
        data.push({id: row.id, state: row.state, number: row.number, expire: moment(row.expire).format('MM/DD/YYYY')});
      });

      return data;
    },

    // Determine if tables are equal to original application data (except empty tables).
    // (field experience, desk experience, certifications, licenses)
    tableIsResolved(duplicateId, tableKey) {

      let duplicateTable = this.tablesData[duplicateId][tableKey];
      let applicationTable = [];

      if (tableKey === 'field_experience')
        applicationTable = this.collectedFieldExperience();
      else if (tableKey === 'desk_experience')
        applicationTable = this.collectedDeskExperience();
      else if (tableKey === 'certifications')
        applicationTable = this.collectedCertifications();
      else if (tableKey === 'licenses')
        applicationTable = this.collectedLicenses();

      if (applicationTable.length !== duplicateTable.length)
        return false;
      else if (applicationTable.length === 0)
        return true;  // If both target and duplicate are empty, then table considered as resolved.

      return this.hashTable(applicationTable) === this.hashTable(duplicateTable);
    },

    originalTableInSelected(tableKey) {
      return Boolean(this.data.selectedTables[tableKey].filter(item => item === this.application.id).length);
    },

    onConfirmCancelMerge() {
      window.location.assign(this.cancelUrl);
    },

    // Handler for error event during submit.
    onSubmitError(errors) {
      this.data.errors = errors;
    },

    // Handler for success event during submit.
    onSubmitSuccess() {
      this.data.errors = {};
      window.location.assign(this.redirectUrl);
    },

  },

}
</script>

<style scoped>
.table-container {
  overflow-x: auto;
}

.w-150 {
  width: 150px;
}

td.left-header {
  vertical-align: middle;
}

del, ins {
  padding: 2px 5px;
  border-radius: 5px;
}

</style>
